/**
 * BOP pressure tests and crew drills ON a hand's tour sheet (fieldiq-api#1324,
 * 0605).
 *
 * The operator's rule (2026-11-02, verbatim): "Field workers (rig manager,
 * driller) can record BOP tests and drills, attached to their own draft tour
 * sheet; office approves them with the sheet. Floorhands view only."
 *
 *   - Everyone who can open the sheet sees its tests and drills, each with a
 *     Pending / Approved badge (the record's own `review_status`). Pending
 *     counts toward no drill warning until the office approves the sheet,
 *     which approves them with it — there is no approve button here or on the
 *     office sheet.
 *   - A hand on THIS sheet's crew as rig manager or driller, while the sheet is
 *     a draft, gets Record BOP test (the shared module's own form, posting to
 *     the sheet's field door) and Record drill (the shared module's own
 *     dialog, likewise). Everyone else gets no button.
 *   - The hand who recorded a pending record may remove it while the sheet is a
 *     draft (the API deletes it). An approved record has no remove.
 *
 * The buttons are drawn from the sheet's crew; the API decides. A refusal is
 * shown in the API's own sentence (403 position_cannot_record names who can
 * record), never the generic permission line.
 *
 * /field/ws/tours/:id/pressure-tests/new is WsFieldTourTestNewScreen below: the
 * shared PressureControlTestNewScreen with a target, so there is one test form.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useRecordWsFieldTourDrill, useRecordWsFieldTourTest, useRemoveWsFieldTourRecord } from '../../../hooks/useWsString';
import { useWsFieldTour } from '../../../hooks/useWsJobs';
import { pcReviewSkin, readDrill, readTest } from '../../../lib/pressure-control';
import { canRecordWsBop, wsBopDefaultInstant, wsBopRefusal, wsDay } from '../../../lib/well-servicing';
import type { PcDrill, PcTest } from '../../../types/pressure-control';
import type { WsFieldTour } from '../../../types/well-servicing';
import PressureControlTestNewScreen from '../../equipment/PressureControlTestNewScreen';
import { RecordDrillDialog } from '../../equipment/PressureControlDrillsScreen';
import { drillLine, testLine } from '../TourLinks';
import { WS_SECONDARY, WsFormError } from '../WsBits';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';

export const wsFieldTourPath = (tourId: string) => `/field/ws/tours/${tourId}`;
export const wsFieldTourTestNewPath = (tourId: string) => `/field/ws/tours/${tourId}/pressure-tests/new`;

type Row = { kind: 'test'; record: PcTest } | { kind: 'drill'; record: PcDrill };

export function WsFieldTourBopSection({ tour }: { tour: WsFieldTour }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId: string | null = user?.id ?? null;
  const canRecord = canRecordWsBop(tour, userId);
  const draft = tour.status === 'draft' && !tour.locked;
  const tz = tour.time_zone ?? null;
  const recordDrill = useRecordWsFieldTourDrill();
  const remove = useRemoveWsFieldTourRecord();
  const [drilling, setDrilling] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const rows: Row[] = useMemo(() => [
    ...(tour.pressure_tests ?? []).map((r) => readTest(r as any)).filter((t): t is PcTest => !!t).map((t) => ({ kind: 'test' as const, record: t })),
    ...(tour.drills ?? []).map((r) => readDrill(r as any)).filter((d): d is PcDrill => !!d).map((d) => ({ kind: 'drill' as const, record: d })),
  ], [tour.pressure_tests, tour.drills]);

  const crewIds = useMemo(() => Array.from(new Set(tour.crew.map((c) => c.user_id).filter(Boolean))), [tour.crew]);
  const defaultAt = wsBopDefaultInstant(tour.start_at, tour.end_at);
  const ref = tour.job_number ? `${tour.job_number} · ${wsDay(tour.work_date)}` : undefined;

  return (
    <section className="space-y-3" data-testid="ws-field-bop" data-can-record={canRecord ? 'true' : 'false'}>
      <h2 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">BOP tests and drills</h2>
      {draft && !canRecord && (
        <p className="text-sm text-fieldiq-muted" data-testid="ws-field-bop-view-only">
          The rig manager or the driller on this sheet records BOP tests and drills. You can view them here.
        </p>
      )}
      {canRecord && (
        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" className={PT_FIELD_PRIMARY} onClick={() => navigate(wsFieldTourTestNewPath(tour.id))} data-testid="ws-field-record-test">
            Record BOP test
          </button>
          <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setDrilling(true)} data-testid="ws-field-record-drill">
            Record drill
          </button>
        </div>
      )}
      <WsFormError message={rowError} />
      {rows.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No BOP test or drill is on this sheet.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const pending = r.record.reviewStatus === 'pending';
            const mine = !!userId && r.record.recordedBy === userId;
            const noun = r.kind === 'test' ? 'pressure test' : 'drill';
            return (
              <li key={`${r.kind}-${r.record.id}`} data-testid={`ws-field-bop-${r.kind}`}>
                <PtFieldCard className="flex items-start justify-between gap-2 px-4 py-3">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-extrabold text-white">{r.kind === 'test' ? 'Pressure test' : 'Drill'}</p>
                    <p className="text-xs text-fieldiq-text">{r.kind === 'test' ? testLine(r.record, tz) : drillLine(r.record, tz)}</p>
                    <PtFieldBadge skin={{ ...pcReviewSkin(r.record.reviewStatus), rail: '' }} testId="ws-field-bop-review" />
                  </div>
                  {draft && pending && mine && (
                    <button
                      type="button"
                      className={WS_SECONDARY}
                      aria-label={`Remove this ${noun}`}
                      disabled={remove.isPending}
                      onClick={() => {
                        setRowError(null);
                        remove.mutate(
                          { tourId: tour.id, kind: r.kind, recordId: r.record.id },
                          {
                            onSuccess: () => toast.success(r.kind === 'test' ? 'Pressure test removed' : 'Drill removed'),
                            onError: (err) => setRowError(wsBopRefusal(err, `The ${noun} could not be removed.`)),
                          },
                        );
                      }}
                    >
                      Remove
                    </button>
                  )}
                </PtFieldCard>
              </li>
            );
          })}
        </ul>
      )}
      {canRecord && (
        <RecordDrillDialog
          open={drilling}
          onClose={() => setDrilling(false)}
          target={{
            title: 'Record a crew BOP drill on this sheet',
            defaultAt,
            defaultCrew: crewIds,
            defaultRef: ref,
            send: async (body) => {
              await recordDrill.mutateAsync({ tourId: tour.id, body });
            },
            refusal: wsBopRefusal,
            successMessage: 'Drill recorded. It is pending until the office approves the sheet.',
            note: 'The drill is pending until the office approves this sheet. A drill is crew training and is not billed.',
          }}
        />
      )}
    </section>
  );
}

/** /field/ws/tours/:id/pressure-tests/new — the shared test form, posting to the sheet. */
export function WsFieldTourTestNewScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useWsFieldTour(id);
  const record = useRecordWsFieldTourTest();
  const tour = query.data?.data ?? null;

  // The route is reachable by URL and by Back, so it asks the same question the
  // sheet's button does. Not drawing the form for a hand the API will refuse
  // also keeps a chart photo (never deletable) from being uploaded for nothing.
  if (tour && id && !canRecordWsBop(tour, user?.id ?? null)) {
    return (
      <PtFieldPage title="Record BOP test" backTo={wsFieldTourPath(id)} testId="ws-field-test-new">
        <PtRefusal
          testId="ws-field-test-new-refused"
          message={tour.status === 'draft' && !tour.locked
            ? 'Only the rig manager or the driller on this sheet can record a BOP test. The rest of the crew can view them on the sheet.'
            : 'This sheet is no longer a draft, so a BOP test cannot be recorded on it.'}
        />
      </PtFieldPage>
    );
  }

  if (!tour || !id) {
    return (
      <PtFieldPage title="Record BOP test" backTo={id ? wsFieldTourPath(id) : '/field/ws'} testId="ws-field-test-new">
        <PtFieldQueryState query={query} subject="this tour sheet">
          <PtRefusal message="That tour sheet no longer exists. It may have been deleted." />
        </PtFieldQueryState>
      </PtFieldPage>
    );
  }

  return (
    <PressureControlTestNewScreen
      key={tour.id}
      target={{
        eyebrow: `Tour sheet · ${wsDay(tour.work_date)}`,
        title: 'Record a BOP test',
        defaultAt: wsBopDefaultInstant(tour.start_at, tour.end_at),
        defaultRef: tour.job_number ? `${tour.job_number} · ${wsDay(tour.work_date)}` : undefined,
        send: async (body) => {
          const out = await record.mutateAsync({ tourId: tour.id, body });
          return { warnings: out.data?.warnings ?? [] };
        },
        refusal: wsBopRefusal,
        successMessage: 'Pressure test recorded. It is pending until the office approves the sheet.',
        done: () => navigate(wsFieldTourPath(tour.id), { replace: true }),
      }}
    />
  );
}
